import React from 'react';

type Event = MouseEvent | TouchEvent;

/** Coppie mouse/touch equivalenti: il trigger sceglie se reagire alla pressione o al rilascio. */
const eventNamesByTrigger = {
  pointerdown: ['mousedown', 'touchstart'],
  pointerup: ['mouseup', 'touchend'],
} as const;

type Trigger = keyof typeof eventNamesByTrigger;

interface Options {
  classesToExclude?: Array<string>;
  /** Se false il listener resta staccato, es. mentre il menu è chiuso. Default true. */
  enabled?: boolean;
  /**
   * Se true (default) i click su target privi di classe non richiamano la callback.
   * Va messo a false quando l'area esterna è testo libero, dove i nodi senza classe abbondano.
   */
  ignoreTargetsWithoutClass?: boolean;
  /** Default 'pointerup'. 'pointerdown' chiude prima che il click raggiunga la pagina. */
  trigger?: Trigger;
}

export const useOutsideClickHandler = <T extends HTMLElement = HTMLElement>(
  ref: React.RefObject<T>,
  callback: () => void,
  options?: Options,
) => {
  const {
    classesToExclude = ['LoadingOverlay'],
    enabled = true,
    ignoreTargetsWithoutClass = true,
    trigger = 'pointerup',
  } = options ?? {};
  const listener = React.useCallback(
    (event: Event) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        const targetClassName = (event.target as Element).classList.value;
        if (!targetClassName) {
          if (!ignoreTargetsWithoutClass) {
            callback();
          }
          return;
        }
        if (!classesToExclude.some((classToExclude) => targetClassName.includes(classToExclude))) {
          callback();
        }
      }
    },
    [callback, classesToExclude, ignoreTargetsWithoutClass, ref],
  );

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const [mouseEventName, touchEventName] = eventNamesByTrigger[trigger];
    document.addEventListener(mouseEventName, listener);
    document.addEventListener(touchEventName, listener);
    return () => {
      document.removeEventListener(mouseEventName, listener);
      document.removeEventListener(touchEventName, listener);
    };
  }, [enabled, listener, trigger]);
};
